import * as stylex from '@stylexjs/stylex';
import { MdVisibilityOff } from 'react-icons/md';
import { styles } from './styles';

// Deterministic tile palette (vivid bg / deep same-hue ink), chosen per user —
// mirrors the Flutter app's AvatarPlaceholder colouring (same order).
const PALETTE = [{
  bg: '#FFB4A2',
  fg: '#9B1C00'
},
// coral
{
  bg: '#FFCE7A',
  fg: '#8A4B00'
},
// marigold
{
  bg: '#FFE066',
  fg: '#7A5A00'
},
// sunshine
{
  bg: '#CDEF84',
  fg: '#4A6B00'
},
// lime
{
  bg: '#96E8B8',
  fg: '#006B3C'
},
// mint
{
  bg: '#7CE4D8',
  fg: '#00655B'
},
// teal
{
  bg: '#8ADCF7',
  fg: '#005D77'
},
// sky
{
  bg: '#A9C7FF',
  fg: '#2038B0'
},
// blue
{
  bg: '#BDB4FF',
  fg: '#3E2DB3'
},
// periwinkle
{
  bg: '#DCB5FF',
  fg: '#6C16B4'
},
// grape
{
  bg: '#FCA8E0',
  fg: '#A8127E'
},
// magenta
{
  bg: '#FFAFC5',
  fg: '#B00D45'
} // rose
];
type Props = {
  // Name → derived initials (when `initials` not given) + colour seed.
  name?: string | null;
  // Precomputed initials (e.g. server-provided, name withheld). Wins over `name`.
  initials?: string | null;
  avatarUrl?: string | null;
  // Masked member — renders a placeholder tile, never any identity.
  hidden?: boolean;
  size?: number; // px
  radius?: number; // px; defaults to a squircle (~0.22 of size, like the app)
};
export function UserAvatar({
  name,
  initials,
  avatarUrl,
  hidden = false,
  size = 44,
  radius
}: Props) {
  const dims = {
    width: size,
    height: size,
    borderRadius: radius ?? Math.round(size * 0.22),
    fontSize: Math.round(size * 0.28)
  };
  if (hidden) {
    const {
      className,
      style
    } = stylex.props(styles.tile, styles.hidden);
    return <div className={className} style={{
      ...style,
      ...dims
    }}>
        <MdVisibilityOff size={Math.round(size * 0.42)} />
      </div>;
  }
  const label = initials ?? toInitials(name);
  const color = PALETTE[seedHash(name ?? label ?? '') % PALETTE.length];
  const {
    className,
    style
  } = stylex.props(styles.tile);
  return <div className={className} style={{
    ...style,
    ...dims,
    backgroundColor: color.bg,
    color: color.fg
  }}>
      {label && <span {...stylex.props(styles.initials)}>{label}</span>}
      {avatarUrl && <img {...stylex.props(styles.img)} src={avatarUrl} alt="" loading="lazy" onError={e => {
      e.currentTarget.style.display = 'none';
    }} />}
    </div>;
}

// First letter of up to the first 3 name parts (matches the app + server).
function toInitials(name?: string | null) {
  if (!name) return null;
  const letters = name.split(/[\s-]+/).filter(Boolean).slice(0, 3).map(part => part.charAt(0).toUpperCase()).join('');
  return letters || null;
}
function seedHash(seed: string) {
  let sum = 0;
  for (let i = 0; i < seed.length; i += 1) sum += seed.charCodeAt(i);
  return sum;
}